/**
 * Pure helpers for the submissions cache used by `useFormsWidgetData`.
 * Cache shape: `{ [formId]: { formId, personId, submissions } }` — one API result per learner +
 * form. Every widget that has the form shows that full list. `reference_id` stays on each
 * submission so the source can be traced later; it does not hide a chip. Attaching the same form
 * to multiple widgets does not repeat the request.
 */

export const EMPTY_RESPONSE_CACHE = Object.freeze({});

const toFormIdSet = (refs = []) =>
  new Set(refs.filter((ref) => ref.formId).map((ref) => ref.formId));

const isEntryCurrent = (entry, formIds, personId) =>
  Boolean(entry) &&
  entry.personId === personId &&
  formIds.has(entry.formId);

const keepCurrentEntries = (entries, formIds, personId) =>
  entries.filter((entry) => isEntryCurrent(entry, formIds, personId));

const toCache = (entries) => Object.fromEntries(entries.map((entry) => [entry.formId, entry]));

/**
 * Drops entries for removed widgets, detached forms, and a different learner.
 * Returns the same object when nothing changed so React can bail out of the update.
 */
export const pruneResponseCache = (cache, refs, personId) => {
  const entries = Object.values(cache);
  const kept = keepCurrentEntries(entries, toFormIdSet(refs), personId);

  return kept.length === entries.length ? cache : toCache(kept);
};

/** One representative ref per attached form whose learner-scoped responses are not cached. */
export const selectPendingRefs = (cache, refs, personId) => {
  const formIds = toFormIdSet(refs);
  const pendingFormIds = new Set(
    [...formIds].filter((formId) => !isEntryCurrent(cache[formId], formIds, personId)),
  );

  return refs.filter((ref) => pendingFormIds.delete(ref.formId));
};

/** Stores fetched form results that are still attached for the current learner; late ones are ignored. */
export const mergeResponseEntries = (cache, fetchedEntries, refs, personId) => {
  const current = keepCurrentEntries(fetchedEntries, toFormIdSet(refs), personId);

  return current.length === 0 ? cache : { ...cache, ...toCache(current) };
};

const groupSubmissionsByWidget = (cache, personId) => (grouped, ref) => {
  const entry = cache[ref.formId];
  if (!entry || entry.personId !== personId) {
    return grouped;
  }

  return {
    ...grouped,
    [ref.widgetId]: {
      ...grouped[ref.widgetId],
      [ref.formId]: entry.submissions,
    },
  };
};

/** `{ [widgetId]: { [formId]: submissions } }` for current entries only. */
export const selectCurrentSubmissions = (cache, refs, personId) =>
  refs.filter((ref) => ref.formId).reduce(groupSubmissionsByWidget(cache, personId), {});
